import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalShell from "@/components/PortalShell";
import { Avatar, StatusPill, rupee, TrustBadge } from "@/components/Bits";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: "Home" },
  { key: "jobs", label: "Job Requests", icon: "ClipboardList" },
  { key: "schedule", label: "My Schedule", icon: "CalendarDays" },
  { key: "documents", label: "Documents", icon: "FileText" },
  { key: "earnings", label: "Earnings", icon: "Wallet" },
  { key: "profile", label: "My Profile", icon: "UserRound" },
];

const DOCS = [
  ["Aadhaar e-KYC", "Verified", "—"], ["PAN Card", "Verified", "—"], ["Police Clearance", "Verified", "Dec 2026"],
  ["BMC Certified", "Verified", "Jun 2027"], ["First Aid Certificate", "Verified", "Mar 2027"], ["Nursing Council Reg.", "Pending", "—"],
];

export default function CarerPortal() {
  const { user } = useAuth();
  const [active, setActive] = useState("dashboard");
  const [carer, setCarer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [bio, setBio] = useState("");

  const load = useCallback(() => {
    api.get("/carers/me/profile").then((r) => { setCarer(r.data); setBio(r.data.bio || ""); }).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/carers/me/settlements").then((r) => setSettlements(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const titles = { dashboard: "Dashboard", jobs: "Job Requests", schedule: "My Schedule", documents: "Documents", earnings: "Earnings", profile: "My Profile" };
  const requests = useMemo(() => bookings.filter((b) => b.status === "Requested"), [bookings]);
  const accepted = useMemo(() => bookings.filter((b) => ["Confirmed", "In Progress", "Completed"].includes(b.status)), [bookings]);

  const accept = async (id) => { try { await api.put(`/bookings/${id}/status`, { status: "Confirmed" }); toast.success("Job accepted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const decline = async (id) => { try { await api.put(`/bookings/${id}/status`, { status: "Cancelled" }); toast("Job declined"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveBio = async () => { try { await api.put("/carers/me", { bio }); toast.success("Profile saved"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const weekEarn = accepted.reduce((s, b) => s + Math.round(b.amount * 0.9), 0);

  return (
    <PortalShell nav={NAV} active={active} setActive={setActive} title={titles[active]} accent="sage">
      {active === "dashboard" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="This week's earnings" value={rupee(weekEarn || 6200)} />
            <Stat label="Active bookings" value={accepted.length} />
            <Stat label="Average rating" value={`${carer?.rating || 4.9} ★`} />
            <Stat label="Acceptance rate" value="96%" />
          </div>
          <div className="card-bmc p-5">
            <h3 className="font-display font-bold mb-4">Pending job requests</h3>
            <div className="space-y-3">
              {requests.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-3 p-4 bg-ivory-2 rounded-r" data-testid={`request-${b.id}`}>
                  <Avatar initials={b.seeker_name.split(" ").map((s) => s[0]).slice(0, 2).join("")} gradient="linear-gradient(140deg,#7C9A78,#5E7C5B)" size={42} />
                  <div className="flex-1 min-w-0"><p className="font-semibold text-sm">{b.service_type} · {b.shift}</p><p className="text-xs text-ink-soft">{b.date} · {b.location} · {rupee(b.amount)}</p></div>
                  <div className="flex gap-2">
                    <button onClick={() => accept(b.id)} className="btn-pill bg-sage text-white px-4 py-2 text-xs" data-testid={`accept-${b.id}`}>Accept</button>
                    <button onClick={() => decline(b.id)} className="btn-pill border border-line px-4 py-2 text-xs" data-testid={`decline-${b.id}`}>Decline</button>
                  </div>
                </div>
              ))}
              {requests.length === 0 && <p className="text-sm text-ink-soft">No pending requests right now.</p>}
            </div>
          </div>
        </div>
      )}

      {active === "jobs" && (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="card-bmc p-5 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0"><p className="font-semibold">{b.service_type} · {b.shift}</p><p className="text-sm text-ink-soft">{b.seeker_name} · {b.date} · {b.location} · {rupee(b.amount)}</p></div>
              <StatusPill status={b.status} />
              {b.status === "Requested" && <div className="flex gap-2"><button onClick={() => accept(b.id)} className="btn-pill bg-sage text-white px-4 py-2 text-xs">Accept</button><button onClick={() => decline(b.id)} className="btn-pill border border-line px-4 py-2 text-xs">Decline</button></div>}
            </div>
          ))}
          {bookings.length === 0 && <p className="text-sm text-ink-soft">No jobs yet.</p>}
        </div>
      )}

      {active === "schedule" && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
            <div key={d} className="card-bmc p-3 min-h-[140px]">
              <p className="font-semibold text-sm text-center border-b border-line-2 pb-2">{d}</p>
              {accepted[i] && <div className="bg-sage-soft text-sage-deep text-xs rounded-r p-2 mt-2">{accepted[i].shift}<br />{accepted[i].location}</div>}
            </div>
          ))}
        </div>
      )}

      {active === "documents" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOCS.map(([name, status, expiry]) => (
            <div key={name} className="card-bmc p-5">
              <div className="flex justify-between items-start"><p className="font-semibold text-sm">{name}</p><StatusPill status={status} /></div>
              <p className="text-xs text-ink-soft mt-2">Expiry: {expiry}</p>
            </div>
          ))}
        </div>
      )}

      {active === "earnings" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-4 gap-4">
            <Stat label="This week" value={rupee(weekEarn || 6200)} />
            <Stat label="This month" value={rupee(24800)} />
            <Stat label="Total earned" value={rupee(carer?.engagements ? carer.engagements * 450 : 153900)} />
            <Stat label="Pending payout" value={rupee(settlements.filter((s) => s.status === "Unsettled").reduce((a, s) => a + s.net, 0))} />
          </div>
          <div className="card-bmc p-5">
            <h3 className="font-display font-bold mb-4">Weekly earnings</h3>
            <div className="flex items-end gap-3 h-40">
              {[3600, 1800, 0, 2400, 4800, 1200, 0].map((v, i) => (
                <div key={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i]} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full bg-coral rounded-t" style={{ height: `${(v / 4800) * 100 || 2}%` }} title={rupee(v)} />
                  <span className="text-xs text-ink-soft">{["M", "T", "W", "T", "F", "S", "S"][i]}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-ink-soft mt-4">Payouts run <b className="text-ink">every Monday</b> to {carer?.bank_name || "your bank"} {carer?.bank_account ? `••••${carer.bank_account.slice(-4)}` : "••••4321"} via IMPS</p>
          </div>
          <div className="card-bmc p-5 overflow-x-auto">
            <h3 className="font-display font-bold mb-4">Settlement history <span className="text-xs font-normal text-ink-soft">· weekly · 8% platform fee · 2% TDS</span></h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink-soft border-b border-line">{["STL ID", "Period", "Jobs", "Gross", "Fee (8%)", "TDS (2%)", "Net", "Status"].map((h) => <th key={h} className="p-3 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} className="border-b border-line-2" data-testid={`carer-settlement-${s.id}`}>
                    <td className="p-3 font-mono text-xs">{s.id}</td><td className="p-3 text-ink-soft text-xs">{s.period}</td><td className="p-3">{s.bookings_count}</td>
                    <td className="p-3">{rupee(s.gross)}</td><td className="p-3 text-ink-soft">{rupee(s.commission)}</td><td className="p-3 text-ink-soft">{rupee(s.tds)}</td>
                    <td className="p-3 font-semibold">{rupee(s.net)}</td><td className="p-3"><StatusPill status={s.status === "Settled" ? "Settled" : "Pending"} /></td>
                  </tr>
                ))}
                {settlements.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-ink-soft">No settlements yet. Completed jobs are settled every Monday.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active === "profile" && carer && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-bmc p-6 text-center">
            <Avatar initials={carer.avatar_initials} gradient={carer.gradient} size={72} />
            <h3 className="text-xl font-bold mt-3">{carer.name}</h3>
            <p className="text-coral font-semibold">{carer.role}</p>
            <div className="flex flex-wrap gap-1.5 justify-center mt-3">{(carer.badges || []).map((b) => <TrustBadge key={b} type={b} />)}</div>
            <p className="text-sm text-ink-soft mt-4">{carer.bio}</p>
          </div>
          <div className="card-bmc p-6">
            <h3 className="font-display font-bold">Edit profile</h3>
            <label className="text-sm font-semibold block mt-4 mb-1.5">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} className="w-full border border-line rounded-r px-3 py-2 text-sm bg-ivory focus:outline-none focus:border-coral resize-none" data-testid="carer-bio" />
            <button onClick={saveBio} className="btn-pill bg-coral text-white px-6 py-2.5 mt-4 flex items-center gap-2" data-testid="save-profile"><CheckCircle2 size={16} /> Save profile</button>
          </div>
        </div>
      )}
    </PortalShell>
  );
}

function Stat({ label, value }) {
  return <div className="card-bmc p-5"><p className="text-sm text-ink-soft">{label}</p><p className="font-display font-bold text-2xl mt-1">{value}</p></div>;
}
